import { useState } from "react";
import { useAtomValue } from "jotai";
import pluralize from "pluralize";
import { Button } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";

import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import { IsMobileAtom } from "../../../App";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useDeleteRecord from "../../../api/api-hooks/records/useDeleteRecord";
import useLockRecord from "../../../api/api-hooks/records/useLockRecord";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useUser from "../../../api/api-hooks/auth/useUser";
import { newToastEvent } from "../../../utils/ToastUtil";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { Module } from "../../../types/TenantInterfaces";
import useDeleteRecordOperations from "../../../api/api-hooks/deletedRecords/DeletedRecordsOperations";
import Constants from "../../../utils/Constants";

interface Props {
  record: any;
  toggleUpdateRecordDrawer: () => void;
  toggleEmailActionsDrawer?: () => void;
  toggleShareRecord?: () => void;
  toggleDtDrawer?: () => void;
  actionsRef?: React.RefObject<any>;
  module: Module;
  onClose?: () => void;
  pageID?: string;
  viewID?: string;
  tab?: string;
}

// Records actions menu, that handles Record actions as well.
const RecordActionsMenu = ({
  record,
  toggleUpdateRecordDrawer,
  toggleEmailActionsDrawer,
  toggleShareRecord,
  toggleDtDrawer,
  actionsRef,
  module,
  onClose,
  pageID = "",
  viewID = "",
  tab = "",
}: Props) => {
  const [copied, setCopied] = useState(false);
  const [copied2, setCopied2] = useState(false);
  const { confirmDelete, deleteRecord } = useDeleteRecord();
  const tenantID = useAtomValue(tenantIDAtom);
  const isMobileScreen = useAtomValue(IsMobileAtom);
  const { lockRecord, lockingRecord } = useLockRecord();
  const { cPermissions } = useCloudPermissions();
  const { user } = useUser();
  const { performOperation } = useDeleteRecordOperations();
  const queryClient = useQueryClient();
  const isGlobal = module?.GlobalModule;
  const isSupportCloud = Constants.SUPPORT_CLOUD_IDS.includes(tenantID!);

  const hasLockRecordPermissions = lockRecordPermissions();

  return (
    <PopoverDropdownMenu>
      <h5 className="px-2 pt-2 text-dark">Actions</h5>
      <DropdownMenuDivider />
      {module && isMobileScreen && (
        <DropdownOptionWithIcon
          name={`Share ${pluralize.singular(module?.Name)}`}
          onClick={toggleShareRecord}
          icon="mdi mdi-share-variant"
          disabled={!record}
        />
      )}
      <DropdownOptionWithIcon
        name="Edit Record"
        onClick={toggleUpdateRecordDrawer}
        icon="mdi mdi-pencil"
        disabled={!record || (isGlobal && !isSupportCloud)}
      />
      <DropdownOptionWithIcon
        name="Open In New Tab"
        onClick={openInNewTab}
        icon="mdi mdi-open-in-new"
        disabled={!record}
      />
      <DropdownOptionWithIcon
        name={copied ? "Drawer URL Copied!" : "Copy Direct URL"}
        onClick={copyLink}
        icon={copied ? "mdi mdi-check-bold" : "mdi mdi-content-copy"}
        disabled={!record}
      />
      <DropdownOptionWithIcon
        name={copied2 ? "JSON  Copied!" : "Copy Raw JSON"}
        onClick={copyRawJSON}
        icon={copied2 ? "mdi mdi-check-bold" : "mdi mdi-code-json"}
        disabled={!record}
      />
      <DropdownOptionWithIcon
        name={
          lockingRecord
            ? `${record?._LockedBy ? "Unlocking" : "Locking"} Record...`
            : record?._LockedBy
            ? "Unlock Record"
            : "Lock Record"
        }
        onClick={() =>
          lockRecord({
            recordID: record?._id || record?.id,
            moduleAPIName: module?.APIName,
            type: record?._LockedBy ? "Unlock" : "Lock",
          })
        }
        icon={
          record?._LockedBy
            ? "mdi mdi-lock-open-variant-outline"
            : "mdi mdi-lock-outline"
        }
        disabled={
          lockingRecord || !hasLockRecordPermissions || !record || isGlobal
        }
      />
      {cPermissions.cloud_update_workflow &&
        toggleEmailActionsDrawer &&
        module?.APIName?.startsWith("GE") && (
          <>
            {/* @ts-ignore */}
            <DropdownOptionWithIcon
              name="Use In Email"
              onClick={toggleEmailActionsDrawer}
              icon="mdi mdi-email-fast-outline"
              disabled={!record}
            />
          </>
        )}

      {module?.DataTransforms &&
        module?.DataTransforms?.length > 0 &&
        module?.APIName !== "Feedback" && (
          <DropdownOptionWithIcon
            name={`Convert Record`}
            onClick={toggleDtDrawer}
            icon="mdi mdi-account-convert-outline"
            disabled={!record}
          />
        )}

      {module?.APIName === "Feedback" && (
        <DropdownOptionWithIcon
          name="Transform into Issue"
          onClick={() => {
            callFunctionIfExists(toggleDtDrawer);
          }}
          disabled={!record}
          customIcon={
            <i
              style={{ fontSize: 18, color: "#5CE500" }}
              className={`mdi mdi-lightning-bolt-circle pr-2`}
            />
          }
        />
      )}

      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Delete Record"
        onClick={() => {
          deleteRecordHandler();
          actionsRef && actionsRef.current.close();
        }}
        icon="mdi mdi-delete"
        disabled={!hasLockRecordPermissions || (isGlobal && !isSupportCloud)}
      />
    </PopoverDropdownMenu>
  );

  function getRecordLink() {
    let url;
    const selectedTab = tab || "Summary";

    if (pageID && viewID) {
      url = `/clouds/${tenantID}/pages/${pageID}/views/${viewID}/records/${
        module?.APIName
      }/details/${record?._id || record?.id}/${selectedTab}`;
    } else {
      url = `/clouds/${tenantID}/records/${module?.APIName}/details/${
        record?._id || record?.id
      }/${selectedTab}`;
    }

    return url;
  }

  function UndoButton({
    recordIDs,
    onSuccess,
  }: {
    recordIDs: string[];
    onSuccess?: () => void;
  }) {
    return (
      <Button
        size="sm"
        onClick={() =>
          performOperation({
            RecordIDs: recordIDs,
            Operation: "Restore",
            onSuccess: () => {
              callFunctionIfExists(onSuccess);
            },
          })
        }
        color="primary"
      >
        Undo
      </Button>
    );
  }

  function copyLink() {
    navigator.clipboard.writeText(
      `${window.location.origin}${getRecordLink()}`
    );
    setCopied(true);

    setTimeout(() => setCopied(false), 3500);
  }

  function copyRawJSON() {
    if (!record) return;
    navigator.clipboard.writeText(JSON.stringify(record, null, 2));
    setCopied2(true);

    setTimeout(() => setCopied2(false), 3500);
  }

  function openInNewTab() {
    window.open(getRecordLink(), "_blank", "noopener,noreferrer");
  }

  async function deleteRecordHandler() {
    const confirmed = await confirmDelete();

    if (!confirmed) return;

    deleteRecord({
      moduleAPIName: module?.APIName,
      recordID: record?._id || record?.id,
      onSuccess: () => {
        callFunctionIfExists(onClose);
        newToastEvent(
          "success",
          <div className="d-flex justify-content-between align-items-center">
            Successfully deleted record.
            {cPermissions.cloud_modify_deleted_records &&
              UndoButton({
                recordIDs: [record?._id || record?.id],
                onSuccess: () => {
                  if (tenantID) {
                    queryClient.invalidateQueries({
                      queryKey: [tenantID, "records", module?.APIName],
                    });
                  }
                },
              })}
          </div>
        );
      },
    });
  }

  function lockRecordPermissions() {
    if (!record) return;
    if (cPermissions.cloud_unlock_records) return true;
    if (record._LockedBy) {
      if (record.CreatedBy.id === user.id || record._LockedBy === user.id) {
        return true;
      }
    } else {
      return true;
    }
    return false;
  }
};

export default RecordActionsMenu;
